import iconsPng from '@/assets/img/png/icons';
import { EMAIL_REGEX, INVALID_PUBLIC_SCREENER_SUBMISSION_ID } from '@/constants/stringVars';
import {
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react';
import React, { useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import useSendResultToEmail from '@/hooks/publicscreener/useSendResultToEmail';
import { TSendResultsToEmailForBE } from '@/types/PublicScreener.types';
import CustomButton from '@/components/atoms/CustomButton';
import colors from '@/theme/colors';
import TurnstileWidget from '@/components/molecules/TurnstileWidget';
import useTelemetry from '@/providers/TelemetryProvider/useTelemetry';
import { mixpanelEvents, PUBLIC_SCREENER_TYPE } from '@/constants/mixpanel';

type Props = {
  submissionId?: string;
  organizationSlug: string;
  isOpen: boolean;
  onClose: () => void;
};

const schema = yup
  .object()
  .shape({
    email: yup.string().matches(EMAIL_REGEX, 'Wrong email format.').required('Email is required.'),
  })
  .required();

const SendDetailedReportToEmailModal = ({ isOpen, onClose, submissionId, organizationSlug }: Props) => {
  const { trackEvent } = useTelemetry();

  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  const { register, formState, handleSubmit } = useForm({
    resolver: yupResolver(schema),
    mode: 'all',
  });

  const { isValid, errors } = formState;

  const {
    mutate: sendResultsToEmail,
    isPending,
    isSuccess,
  } = useSendResultToEmail(turnstileToken, (email) => {
    onClose();

    trackEvent(mixpanelEvents.SCREENER_RESULTS_EMAILED, { email, type: PUBLIC_SCREENER_TYPE });
  });

  type EmailFormData = {
    email: string;
  };

  const onSubmit = (data: EmailFormData) => {
    trackEvent(mixpanelEvents.SCREENER_EMAIL_SUBMITTED, { email: data.email, type: PUBLIC_SCREENER_TYPE });

    const formData: TSendResultsToEmailForBE = {
      email: data.email,
      organizationSlug,
      submissionId: submissionId ?? INVALID_PUBLIC_SCREENER_SUBMISSION_ID,
    };

    sendResultsToEmail(formData);
  };

  return (
    <Modal onClose={onClose} isOpen={isOpen} isCentered closeOnOverlayClick={true}>
      <ModalOverlay bg="blackAlpha.700" backdropFilter={'blur(18px)'} />
      <ModalContent
        borderRadius={'20px'}
        backgroundColor={'background.lightBlue'}
        minWidth={'360px'}
        maxWidth={'380px'}
        overflowY={'hidden'}
      >
        <ModalCloseButton
          size={'lg'}
          width={'2.2rem'}
          height={'2.2rem'}
          marginTop={'15px'}
          marginRight={'10px'}
          color={'black'}
        />
        <ModalBody marginTop={'48px'} textAlign={'center'}>
          <VStack gap={'40px'} paddingY={'10px'}>
            <Image alt="ok-hand" src={iconsPng.okHand} width={140} height={140} />
            <VStack gap={'10px'}>
              <Text variant={'loraExtraSmallTitle'}>Get your detailed report for each domain.</Text>
              <Text variant={'urbanistSemiBold'}>
                Provide your email and we'll send you your MyOmnia Wholeness Report.
              </Text>
            </VStack>
            <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
              <VStack gap={'10px'}>
                <Input
                  maxWidth={'100%'}
                  width={'100%'}
                  height={'54px'}
                  {...register('email')}
                  backgroundColor={'white'}
                  placeholder="Your Email Address"
                  _placeholder={{
                    fontWeight: 600,
                    color: colors.text.mediumGray,
                  }}
                  autoComplete="off"
                />
                {!isValid && <Text variant={'error'}>{errors.email?.message}</Text>}
                <CustomButton
                  label={isSuccess ? 'Results Emailed' : 'Send me my Results'}
                  backgroundColor="secondary.500"
                  labelColor="extra.white"
                  disabled={!isValid || isSuccess || !turnstileToken}
                  buttonType="submit"
                  width={'100%'}
                  isLoading={isPending}
                />
                <Text
                  variant={'urbanistSemiBold'}
                  color={'text.mediumGray'}
                  fontSize={'14px'}
                  mt={'10px'}
                  maxWidth={'100%'}
                >
                  By clicking Send me my Results you consent to receiving wholeness related emails
                </Text>
                <TurnstileWidget setTurnstileToken={setTurnstileToken} turnstileToken={turnstileToken} />
              </VStack>
            </form>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default SendDetailedReportToEmailModal;
